/**
 * Shared Open Graph / Twitter image helpers.
 *
 * - `OG_VERSION` busts the WhatsApp/Facebook scrape cache: bump it whenever a
 *   social image changes so crawlers re-fetch a brand-new URL.
 * - The corporate social image (`/og/og-home-v2.jpg`) is a real authorised
 *   photograph with the Mostrando México logo and the claim
 *   "Tours y experiencias en México".
 * - Tour pages get a dynamic 1200x630 built from the real API photo.
 */
export const SITE_URL = "https://www.mostrandomexico.mx";

/** Bump to force Facebook/WhatsApp/X to re-scrape the social images. */
export const OG_VERSION = "5";

export const OG_FALLBACK_PATH = "/og/og-home-v2.jpg";

/** Absolute, public, HTTPS corporate social image (no session, no signing). */
export const OG_CORPORATE_IMAGE = `${SITE_URL}${OG_FALLBACK_PATH}?v=${OG_VERSION}`;

/** Corporate logo used as an overlay inside the dynamic tour image. */
export const OG_LOGO_URL = `${SITE_URL}/__l5e/assets-v1/bef23d99-b8f3-4058-a396-6d7ffed005bc/mostrando-mexico-logo-full-transparent.png`;

/**
 * Hosts whose photographs may be composed into a social image. Keeps the
 * public endpoint from being used as an open image proxy.
 */
const ALLOWED_OG_PHOTO_HOSTS = [
  "bokun.s3.amazonaws.com",
  "s3.amazonaws.com",
  "images.bokun.is",
  "bokun.imgix.net",
  "wsrv.nl",
  "www.mostrandomexico.mx",
  "mostrandomexico.mx",
  "mostrandomexico.lovable.app",
  "lovable.app",
];

export function isAllowedOgPhotoHost(url: string): boolean {
  try {
    const u = new URL(url);
    if (u.protocol !== "https:") return false;
    const host = u.hostname.toLowerCase();
    return ALLOWED_OG_PHOTO_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
  } catch {
    return false;
  }
}

/** The real photo cropped to exactly 1200x630 through the image CDN. */
export function ogPhotoCoverUrl(url: string): string {
  if (new URL(url).hostname.toLowerCase().endsWith("wsrv.nl")) return url;
  const p = new URLSearchParams({
    url: url.replace(/^https:\/\//i, ""),
    w: "1200",
    h: "630",
    fit: "cover",
    output: "jpg",
    q: "82",
  });
  return `https://wsrv.nl/?${p.toString()}`;
}

/** Corporate logo shrunk to overlay size so the SVG stays small. */
export function ogLogoSmallUrl(): string {
  const p = new URLSearchParams({
    url: OG_LOGO_URL.replace(/^https:\/\//i, ""),
    w: "360",
    output: "png",
  });
  return `https://wsrv.nl/?${p.toString()}`;
}

/** Shorten "Mismaloya, Puerto Vallarta, Jalisco, Mexico" to "Puerto Vallarta, Jalisco". */
export function shortPlace(place?: string | null): string {
  if (!place) return "";
  const parts = place
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s && !/^m[eé]xico$/i.test(s));
  if (parts.length <= 2) return parts.join(", ");
  return parts.slice(-2).join(", ");
}

/** Fix stray spaces before punctuation ("Bacalar , Laguna" → "Bacalar, Laguna"). */
export function tidyText(s: string): string {
  return s.replace(/\s+([,.;:!?])/g, "$1").replace(/\s{2,}/g, " ").trim();
}

/**
 * Destinations recognised in the tour title. Bókun often stores the pickup
 * city (e.g. "Cancún") as the location, so the title wins when it names a
 * different destination.
 */
const TITLE_DESTINATIONS: Array<{ re: RegExp; place: string; tagline: string }> = [
  { re: /bacalar/i, place: "Bacalar, Quintana Roo", tagline: "La laguna de los siete colores te espera" },
  { re: /holbox/i, place: "Holbox, Quintana Roo", tagline: "Isla de aguas turquesa y calma total" },
  { re: /chich[eé]n/i, place: "Chichén Itzá, Yucatán", tagline: "Maravilla del mundo maya" },
  { re: /tulum/i, place: "Tulum, Quintana Roo", tagline: "Ruinas mayas frente al Caribe" },
  { re: /isla mujeres/i, place: "Isla Mujeres, Quintana Roo", tagline: "Playas de arena blanca y mar turquesa" },
  { re: /cozumel/i, place: "Cozumel, Quintana Roo", tagline: "Arrecifes y aguas cristalinas" },
  { re: /valladolid/i, place: "Valladolid, Yucatán", tagline: "Cenotes y encanto colonial" },
  { re: /las coloradas/i, place: "Las Coloradas, Yucatán", tagline: "Lagunas rosas junto al mar" },
  { re: /teotihuac[aá]n/i, place: "Teotihuacán, Estado de México", tagline: "La ciudad de los dioses" },
];

export function resolveOgPlace(title?: string | null, place?: string | null): { place: string; tagline: string } {
  const t = title ?? "";
  for (const d of TITLE_DESTINATIONS) if (d.re.test(t)) return { place: d.place, tagline: d.tagline };
  const p = shortPlace(place);
  return { place: p, tagline: "Vive México con expertos locales" };
}

/** Prefer a real photo (JPEG) over PNG graphics, which may carry overlays. */
export function pickOgPhoto(images?: string[] | null): string | undefined {
  const list = (images ?? []).filter(Boolean);
  return list.find((u) => /\.jpe?g(\?|$)/i.test(u)) ?? list[0];
}

/**
 * Absolute URL of the social image for a tour: the real product photo with a
 * gradient, a short tagline, the destination and the logo composed over it.
 * Falls back to the corporate image when the product has no photo.
 */
export function ogTourImage(input: {
  image?: string | null;
  title?: string | null;
  place?: string | null;
}): string {
  const img = (input.image ?? "").trim();
  if (!img || !isAllowedOgPhotoHost(img)) return OG_CORPORATE_IMAGE;
  const p = new URLSearchParams({ img, v: OG_VERSION });
  const { place, tagline } = resolveOgPlace(input.title, input.place);
  p.set("t", tagline);
  if (place) p.set("d", place);
  return `${SITE_URL}/api/public/og/tour?${p.toString()}`;
}
